import { CabButton, CabDropdown } from "@CabComponents";
import { CabModal } from "@CabComponents/CabModal";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { Alert, Box, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { ReactElement, useMemo, useState } from "react";
import colors from "../../colors";
import { RecurrenceRule, UICalendarEventConsolidated } from "../../store";

const weekdayByLuxonNumber: Record<number, string> = {
  1: "MO",
  2: "TU",
  3: "WE",
  4: "TH",
  5: "FR",
  6: "SA",
  7: "SU",
};

const weekdayLabelByLuxonNumber: Record<number, string> = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

type RecurrencePeriod = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

const periodOptions: { value: RecurrencePeriod; label: string }[] = [
  { value: "DAILY", label: "day" },
  { value: "WEEKLY", label: "week" },
  { value: "MONTHLY", label: "month" },
  { value: "YEARLY", label: "year" },
];

const periodLabel = (period: RecurrencePeriod, interval: number) => {
  const label = periodOptions.find(option => option.value === period)?.label || "week";
  return interval === 1 ? label : `${label}s`;
};

const plusPeriod = (date: DateTime, period: RecurrencePeriod, interval: number) => {
  if (period === "DAILY") return date.plus({ days: interval });
  if (period === "MONTHLY") return date.plus({ months: interval });
  if (period === "YEARLY") return date.plus({ years: interval });
  return date.plus({ weeks: interval });
};

export type RecurrenceModalProps = {
  open: boolean;
  start: DateTime;
  end: DateTime;
  timezone: string;
  calendarEvents: UICalendarEventConsolidated[];
  calendarEventsLoading?: boolean;
  conflictCheckEndDate?: DateTime;
  initialRule?: RecurrenceRule;
  onClose: () => void;
  onConfirm: (rule: RecurrenceRule) => void;
  onRemove?: () => void;
};

const formatUntil = (date: DateTime, timezone: string) => (
  date.setZone(timezone).endOf("day").toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'")
);

const datesOverlap = (firstStart: DateTime, firstEnd: DateTime, secondStart: DateTime, secondEnd: DateTime) => (
  firstStart < secondEnd && firstEnd > secondStart
);

const parseRuleParts = (rule?: RecurrenceRule) => {
  if (!rule?.rrule) return {};
  return Object.fromEntries(rule.rrule.replace(/^RRULE:/i, "").split(";").map(part => {
    const [key, value] = part.split("=");
    return [key, value];
  }));
};

const parsePeriod = (freq?: string): RecurrencePeriod => {
  if (freq === "DAILY" || freq === "WEEKLY" || freq === "MONTHLY" || freq === "YEARLY") return freq;
  return "WEEKLY";
};

const parseUntilDate = (until?: string, timezone?: string) => {
  if (!until) return null;
  const parsed = DateTime.fromFormat(until.replace(/Z$/, ""), "yyyyMMdd'T'HHmmss", { zone: "UTC" });
  return parsed.isValid ? parsed.setZone(timezone || "UTC") : null;
};

const RecurrenceModal = ({
  open, start, end, timezone, calendarEvents, calendarEventsLoading, conflictCheckEndDate, initialRule, onClose,
  onConfirm, onRemove
}: RecurrenceModalProps): ReactElement => {
  const zonedStart = start.setZone(timezone);
  const zonedEnd = end.setZone(timezone);
  const durationMinutes = Math.max(1, Math.round(zonedEnd.diff(zonedStart, "minutes").minutes));
  const initialRuleParts = useMemo(() => parseRuleParts(initialRule), [initialRule]);
  const [interval, setInterval] = useState(Number(initialRuleParts.INTERVAL || 1));
  const [period, setPeriod] = useState<RecurrencePeriod>(parsePeriod(initialRuleParts.FREQ));
  const [endType, setEndType] = useState<"until" | "indefinite" | "count">(
    initialRuleParts.UNTIL ? "until" : initialRuleParts.COUNT || !initialRule ? "count" : "indefinite"
  );
  const [count, setCount] = useState(Number(initialRuleParts.COUNT || 8));
  const [untilDate, setUntilDate] = useState<DateTime | null>(
    parseUntilDate(initialRuleParts.UNTIL, timezone) || zonedStart.plus({ weeks: 8 })
  );

  const updateCount = (value: string) => {
    const nextCount = Number(value);
    if (!Number.isFinite(nextCount)) return;
    setCount(Math.max(1, Math.min(52, nextCount)));
  };

  const recurrenceDates = useMemo(() => {
    const maxPreviewCount = endType === "count" ? count : 52;
    const dates: DateTime[] = [];
    let cur = zonedStart;
    while (dates.length < maxPreviewCount) {
      if (endType === "until" && untilDate && cur > untilDate.endOf("day")) break;
      dates.push(cur);
      cur = plusPeriod(cur, period, interval);
    }
    return dates;
  }, [count, endType, interval, period, untilDate, zonedStart]);

  const previewDates = useMemo(() => recurrenceDates.slice(0, 4), [recurrenceDates]);
  const hiddenPreviewCount = endType === "indefinite" ? 0 : recurrenceDates.length - previewDates.length;

  const busyCalendarEvents = useMemo(() => (
    calendarEvents
      .filter(event => event.busy && !event.isBackground && !event.isCanceled)
      .map(event => {
        const eventStart = DateTime.fromISO(event.start).setZone(timezone);
        const eventEnd = DateTime.fromISO(event.end).setZone(timezone);
        return { event, eventStart, eventEnd };
      })
      .filter(({ eventStart, eventEnd }) => eventStart.isValid && eventEnd.isValid)
  ), [calendarEvents, timezone]);

  const conflictDates = useMemo(() => (
    conflictCheckEndDate
      ? recurrenceDates.filter(date => date <= conflictCheckEndDate.setZone(timezone).endOf("day"))
      : recurrenceDates
  ), [conflictCheckEndDate, recurrenceDates, timezone]);

  const recurrenceConflicts = useMemo(() => (
    conflictDates.flatMap(recurrenceStart => {
      const recurrenceEnd = recurrenceStart.plus({ minutes: durationMinutes });
      return busyCalendarEvents.filter(({ eventStart, eventEnd }) => (
        datesOverlap(recurrenceStart, recurrenceEnd, eventStart, eventEnd)
      )).map(({ event }) => ({
        occurrenceStart: recurrenceStart,
        occurrenceEnd: recurrenceEnd,
        event,
      }));
    })
  ), [busyCalendarEvents, conflictDates, durationMinutes]);

  const recurrenceSummary = useMemo(() => {
    const weekday = weekdayLabelByLuxonNumber[zonedStart.weekday];
    const intervalText = `Every ${interval === 1 ? "" : `${interval} `}${periodLabel(period, interval)}`;
    const periodQualifier = period === "WEEKLY" ? ` on ${weekday}`
      : period === "MONTHLY" ? ` on day ${zonedStart.day}`
        : period === "YEARLY" ? ` on ${zonedStart.toFormat("MMM d")}` : "";
    const endText = endType === "count"
      ? `for ${count} occurrence${count === 1 ? "" : "s"}`
      : endType === "until" && untilDate ? `until ${untilDate.toFormat("MMM d, yyyy")}` : "indefinitely";
    return `${intervalText}${periodQualifier} ${endText}`.trim();
  }, [count, endType, interval, period, untilDate, zonedStart]);

  const handleConfirm = () => {
    const endRulePart = endType === "count"
      ? `COUNT=${count}`
      : endType === "until" && untilDate ? `UNTIL=${formatUntil(untilDate, timezone)}` : "";
    const parts = [
      `FREQ=${period}`,
      `INTERVAL=${interval}`,
      period === "WEEKLY" ? `BYDAY=${weekdayByLuxonNumber[zonedStart.weekday]}` : "",
      period === "MONTHLY" ? `BYMONTHDAY=${zonedStart.day}` : "",
      period === "YEARLY" ? `BYMONTH=${zonedStart.month}` : "",
      period === "YEARLY" ? `BYMONTHDAY=${zonedStart.day}` : "",
      endRulePart,
    ].filter(Boolean);

    onConfirm({
      dtstart: zonedStart.toISO() || "",
      timezone,
      duration_minutes: durationMinutes,
      rrule: `RRULE:${parts.join(";")}`,
      exdates: initialRule?.exdates || [],
      rdates: initialRule?.rdates || [],
      supported_ui_version: 1,
      summary: recurrenceSummary,
      provider_series: initialRule?.provider_series || {},
    });
    onClose();
  };

  const conflictAlertSeverity = calendarEventsLoading ? "info" : recurrenceConflicts.length > 0 ? "warning" : "success";
  const conflictAlertMessage = calendarEventsLoading
    ? "Checking calendar events for conflicts."
    : recurrenceConflicts.length > 0
      ? `${recurrenceConflicts.length} conflict${recurrenceConflicts.length === 1 ? "" : "s"} found.`
      : conflictCheckEndDate
        ? `No conflicts found through ${conflictCheckEndDate.setZone(timezone).toFormat("MMM d, yyyy")}.`
        : "No conflicts found.";

  return (
    <CabModal
      open={open}
      onClose={onClose}
      title={initialRule ? "Edit Recurrence Option" : "Add Recurrence Option"}
      closeIcon
      sx={{
        '& .MuiDialog-paper': {
          width: 560,
          maxWidth: 560,
        },
      }}
      actionButtons={(
        <>
          <CabButton buttonType="tertiary" onClick={onClose}>
            Cancel
          </CabButton>
          {initialRule && onRemove && (
            <CabButton buttonType="primary" color="error" onClick={onRemove}>
              Remove
            </CabButton>
          )}
          <CabButton buttonType="primary" onClick={handleConfirm}
            disabled={endType === "until" && !untilDate}>
            Save
          </CabButton>
        </>
      )}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color={colors.black700}>
          Starting {zonedStart.toFormat("cccc, MMMM d, h:mm a")} to {zonedEnd.toFormat("h:mm a")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2">Repeat every</Typography>
          <CabDropdown<number>
            value={interval}
            onChange={event => setInterval(Number(event.target.value))}
            options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(value => ({ value, label: value.toString() }))}
            sx={{ width: 86 }}
          />
          <CabDropdown<RecurrencePeriod>
            value={period}
            onChange={event => setPeriod(event.target.value as RecurrencePeriod)}
            options={periodOptions}
            sx={{ width: 120 }}
          />
        </Box>

        <RadioGroup
          value={endType}
          onChange={event => setEndType(event.target.value as "until" | "indefinite" | "count")}
        >
          <FormControlLabel
            value="until"
            control={<Radio />}
            label={<Typography variant="body2">Ending on date</Typography>}
          />
          {endType === "until" && (
            <Box sx={{ marginLeft: 4, marginTop: 1, width: 250 }}>
              <DatePicker
                value={untilDate}
                onChange={setUntilDate}
                minDate={zonedStart}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
          )}
          <FormControlLabel
            value="indefinite"
            control={<Radio />}
            label={<Typography variant="body2">Indefinite</Typography>}
          />
          <FormControlLabel
            value="count"
            control={<Radio />}
            label={(
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Ending after</Typography>
                <CabTextInput
                  type="number"
                  size="small"
                  value={count.toString()}
                  disabled={endType !== "count"}
                  onChange={event => updateCount(event.target.value)}
                  sx={{ width: 86 }}
                />
                <Typography variant="body2">occurrences</Typography>
              </Box>
            )}
          />
        </RadioGroup>

        <Alert severity={conflictAlertSeverity}>{conflictAlertMessage}</Alert>

        {recurrenceConflicts.length > 0 && (
          <Box>
            <Typography variant="h2">Conflicts</Typography>
            <Box component="ul" sx={{ margin: "8px 0 0", paddingLeft: 3 }}>
              {recurrenceConflicts.slice(0, 8).map(({ occurrenceStart, event }) => (
                <Typography component="li" variant="body2" key={`${occurrenceStart.toISO()}-${event.unique_id}`}>
                  {occurrenceStart.toFormat("EEE, MMM d, h:mm a")} conflicts with {event.title || "Busy event"}
                </Typography>
              ))}
            </Box>
            {recurrenceConflicts.length > 8 && (
              <Typography variant="caption" color={colors.black700}>
                {recurrenceConflicts.length - 8} more conflict{recurrenceConflicts.length - 8 === 1 ? "" : "s"}.
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <Typography variant="h2">Preview</Typography>
          <Typography variant="body2" color={colors.black700}>{recurrenceSummary}</Typography>
          <Box component="ul" sx={{ margin: "8px 0 0", paddingLeft: 3 }}>
            {previewDates.map(date => (
              <Typography component="li" variant="body2" key={date.toISO()}>
                {date.toFormat("EEE, MMM d, yyyy 'at' h:mm a")}
              </Typography>
            ))}
          </Box>
          {hiddenPreviewCount > 0 && (
            <Typography variant="body2" color={colors.black700} sx={{ marginLeft: 1, marginTop: 0.25 }}>
              [{hiddenPreviewCount} more {hiddenPreviewCount === 1 ? "occurrence" : "occurrences"}...]
            </Typography>
          )}
        </Box>
      </Box>
    </CabModal>
  );
};

export default RecurrenceModal;
